import React, { type HtmlHTMLAttributes, useEffect } from 'react';
import { animated, type UseTransitionProps, useTransition } from '@react-spring/web';
import { useDrawerInstance, useDrawerStore } from '../hooks/useDrawer';
import type { BaseDrawerViewProps, CenterDrawerViewProps, Position } from '../shared/types';
import DrawerViewComponent from './DrawerViewComponent';

const transitionRightConfig: UseTransitionProps = {
    from: { x: '100%' },
    enter: { x: '0%' },
    leave: { x: '100%' }
};

const transitionLeftConfig: UseTransitionProps = {
    from: { x: '-100%' },
    enter: { x: '0%' },
    leave: { x: '-100%' }
};

const transitionCenterConfig: UseTransitionProps = {
    from: { y: '25%', x: '-50%', opacity: 0 },
    enter: { y: '-50%', x: '-50%', opacity: 1 },
    leave: { y: '25%', x: '-50%', opacity: 0 }
};

const transitionBottomConfig: UseTransitionProps = {
    from: { y: '100%' },
    enter: { y: '0%' },
    leave: { y: '100%' }
};

const getTransitionConfig = (position: Position): UseTransitionProps => {
    switch (position) {
        case 'right':
            return transitionRightConfig;
        case 'left':
            return transitionLeftConfig;
        case 'center':
            return transitionCenterConfig;
        case 'bottom':
            return transitionBottomConfig;
        default:
            return transitionCenterConfig;
    }
};

const AnimatedComponent = animated<any>(DrawerViewComponent);

type AnimatedDrawerViewProps = HtmlHTMLAttributes<HTMLDivElement> &
    Pick<BaseDrawerViewProps, 'id' | 'animate' | 'children'> & {
        isOpen: boolean;
        position: Position;
        fullscreen: boolean;
        size?: CenterDrawerViewProps['size'];
    };

const AnimatedDrawerViewComponent = React.forwardRef<HTMLDivElement, AnimatedDrawerViewProps>((props, ref) => {
    const { isOpen, animate, fullscreen, position, children, size, id, ...restProps } = props;
    const drawerInstances = useDrawerStore((state) => state.drawerInstances);
    const drawerInstance = useDrawerInstance(id);
    const [firstDrawer, setFirstDrawer] = React.useState(drawerInstances.length === 0);

    // Always animate the first drawer, subsequent drawers will not animate if animate is false
    const skipAnimation = firstDrawer ? false : !animate;

    const transitionConfig: UseTransitionProps = {
        ...getTransitionConfig(position),
        onRest: () => {
            if (drawerInstance?.animation) {
                drawerInstance.animation.resolve();
            }
        },
        immediate: skipAnimation
    };

    const drawerTransition = useTransition(isOpen, transitionConfig);

    useEffect(() => {
        setFirstDrawer(drawerInstances.length === 0);
    }, [drawerInstances]);

    return drawerTransition(
        (styles, active) =>
            active && (
                <AnimatedComponent
                    ref={ref}
                    style={styles}
                    $position={position}
                    $size={size}
                    $fullscreen={fullscreen}
                    {...restProps}
                >
                    {children}
                </AnimatedComponent>
            )
    );
});

export default AnimatedDrawerViewComponent;
